import { api } from "./api";
import type {
  GlobalSearchResponse,
  SearchFilters,
  AdvancedSearchResponse,
  FacetResponse,
  SuggestionResponse,
} from "./searchApi";

export interface CourseFilters {
  page?: number;
  limit?: number;
  category?: string;
  difficulty?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  priceMin?: number;
  priceMax?: number;
  rating?: number;
  isPublished?: boolean;
  isFeatured?: boolean;
}

export interface CourseInstructor {
  _id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
}

export interface Course {
  _id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription: string;
  instructor: CourseInstructor;
  category: string;
  subCategory?: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  language: string;
  thumbnail: string;
  thumbnailUrl?: string;
  promoVideo?: string;
  price: number;
  originalPrice?: number;
  currency: string;
  discount?: number;
  whatYouWillLearn: string[];
  requirements: string[];
  prerequisites?: string;
  enrolledStudents: number;
  rating: number;
  reviewsCount: number;
  certificate: boolean;
  downloadableResources?: number;
  articlesCount?: number;
  mobileAccess: boolean;
  tags: string[];
  isPublished: boolean;
  isFeatured: boolean;
  isPopular: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CourseStats {
  totalEnrollments: number;
  activeEnrollments: number;
  completedEnrollments: number;
  completionRate: number;
  totalReviews: number;
  averageRating: number;
  totalModules: number;
  totalLessons: number;
  totalDuration: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface CourseResponse {
  success: boolean;
  count: number;
  data: Course[];
  pagination: Pagination;
}

export interface SingleCourseResponse {
  success: boolean;
  data: Course;
}

export interface CourseStatsResponse {
  success: boolean;
  data: {
    course: Course;
    statistics: CourseStats;
  };
}

const buildQueryString = (filters?: Record<string, any>): string => {
  if (!filters) return "";
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

export const coursesApi = {
  // ==================== COURSE MANAGEMENT ====================
  getCourses: (filters?: CourseFilters) => {
    const query = buildQueryString(filters);
    return api.get<CourseResponse>(`/courses${query ? `?${query}` : ""}`);
  },

  getCourseById: (id: string) =>
    api.get<SingleCourseResponse>(`/courses/${id}`),

  getMyEnrolledCourses: (filters?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    const query = buildQueryString(filters);
    return api.get<CourseResponse>(
      `/courses/my-enrolled${query ? `?${query}` : ""}`,
    );
  },

  getInstructorCourses: (
    instructorId: string,
    filters?: { page?: number; limit?: number; isPublished?: boolean },
  ) => {
    const query = buildQueryString(filters);
    return api.get<CourseResponse>(
      `/courses/instructor/${instructorId}${query ? `?${query}` : ""}`,
    );
  },

  createCourse: (data: {
    title: string;
    description: string;
    shortDescription: string;
    category: string;
    difficulty: string;
    price: number;
    thumbnailUrl?: string;
    promoVideo?: string;
    originalPrice?: number;
    currency?: string;
    discount?: number;
    whatYouWillLearn?: string[];
    requirements?: string[];
    prerequisites?: string;
    language?: string;
    tags?: string[];
    isPublished?: boolean;
  }) =>
    api.post<{ success: boolean; message: string; data: Course }>(
      "/courses",
      data,
    ),

  updateCourse: (
    id: string,
    data: Partial<{
      title: string;
      description: string;
      shortDescription: string;
      category: string;
      difficulty: string;
      thumbnailUrl: string;
      promoVideo: string;
      price: number;
      originalPrice: number;
      discount: number;
      whatYouWillLearn: string[];
      requirements: string[];
      prerequisites: string;
      tags: string[];
      isPublished: boolean;
      isFeatured: boolean;
    }>,
  ) =>
    api.put<{ success: boolean; message: string; data: Course }>(
      `/courses/${id}`,
      data,
    ),

  deleteCourse: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/courses/${id}`),

  publishCourse: (id: string) =>
    api.put<{ success: boolean; message: string }>(`/courses/${id}/publish`),

  unpublishCourse: (id: string) =>
    api.put<{ success: boolean; message: string }>(`/courses/${id}/unpublish`),

  getCourseStats: (id: string) =>
    api.get<CourseStatsResponse>(`/courses/${id}/stats`),

  getCourseAnalytics: (id: string) =>
    api.get<{ success: boolean; data: { course: Course; analytics: any } }>(
      `/courses/${id}/analytics`,
    ),

  uploadThumbnail: (file: File) =>
    api.uploadFile<{
      success: boolean;
      message: string;
      data: { url: string };
    }>("/upload/course-thumbnail", file, "thumbnail"),

  getAllCourses: (options?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.status) params.append("status", options.status);
    if (options?.search) params.append("search", options.search);

    return api.get<{
      success: boolean;
      courses: Course[];
      data: Course[];
      pagination: any;
    }>(`/courses/all${params.toString() ? `?${params.toString()}` : ""}`);
  },

  getStats: () => {
    return api.get<{
      success: boolean;
      stats: {
        totalCourses: number;
        publishedCourses: number;
        draftCourses: number;
        archivedCourses: number;
      };
    }>("/courses/stats");
  },

  // ==================== SEARCH METHODS ====================
  /**
   * Global search across all platform content
   * @param query - Search query string (min 2 characters)
   * @param type - Filter by content type (optional)
   * @param page - Page number
   * @param limit - Items per page
   */
  globalSearch: async (
    query: string,
    type?: "courses" | "forums" | "discussions" | "mentors",
    page: number = 1,
    limit: number = 12,
  ): Promise<GlobalSearchResponse> => {
    const params = new URLSearchParams();
    params.append("q", query);
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (type) params.append("type", type);

    const response = await api.get<{
      success: boolean;
      data: GlobalSearchResponse;
    }>(`/search/global?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }
    return {
      courses: [],
      forums: [],
      discussions: [],
      mentors: [],
      pagination: { page: 1, limit, total: 0, pages: 0 },
    };
  },

  /**
   * Advanced course search with filters
   * @param query - Search query
   * @param filters - Search filters
   */
  advancedSearch: async (
    query: string,
    filters: SearchFilters = {},
  ): Promise<AdvancedSearchResponse> => {
    const params = new URLSearchParams();
    if (query && query.length >= 2) params.append("q", query);
    if (filters.category) params.append("category", filters.category);
    if (filters.subCategory) params.append("subCategory", filters.subCategory);
    if (filters.difficulty) params.append("difficulty", filters.difficulty);
    if (filters.language) params.append("language", filters.language);
    if (filters.priceMin !== undefined)
      params.append("priceMin", filters.priceMin.toString());
    if (filters.priceMax !== undefined)
      params.append("priceMax", filters.priceMax.toString());
    if (filters.priceType && filters.priceType !== "all")
      params.append("priceType", filters.priceType);
    if (filters.rating) params.append("rating", filters.rating.toString());
    if (filters.tags && filters.tags.length)
      params.append("tags", filters.tags.join(","));
    if (filters.features && filters.features.length)
      params.append("features", filters.features.join(","));
    if (filters.duration) params.append("duration", filters.duration);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());

    const response = await api.get<{
      success: boolean;
      data: AdvancedSearchResponse;
    }>(`/search?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }
    return {
      courses: [],
      pagination: { page: 1, limit: 12, total: 0, pages: 0 },
    };
  },

  /**
   * Get search facets/filters with counts
   */
  getSearchFacets: async (): Promise<FacetResponse> => {
    const response = await api.get<{ success: boolean; data: FacetResponse }>(
      "/search/facets",
    );
    if (response.success && response.data) {
      return response.data;
    }
    return { categories: [], difficulties: [], priceRanges: [], ratings: [] };
  },

  /**
   * Get popular searches (trending courses)
   * @param limit - Number of results
   */
  getPopularSearches: async (limit: number = 10): Promise<Course[]> => {
    const response = await api.get<{ success: boolean; data: Course[] }>(
      `/search/popular?limit=${limit}`,
    );
    return response.success ? response.data : [];
  },

  /**
   * Get autocomplete suggestions
   * @param query - Search query (min 2 characters)
   * @param limit - Number of suggestions
   */
  getSearchSuggestions: async (
    query: string,
    limit: number = 10,
  ): Promise<SuggestionResponse> => {
    const response = await api.get<{
      success: boolean;
      data: SuggestionResponse;
    }>(`/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`);
    if (response.success && response.data) {
      return response.data;
    }
    return { suggestions: [], courses: [] };
  },

  /**
   * Get related courses based on a course
   * @param courseId - Course ID
   * @param limit - Number of results
   */
  getRelatedCourses: async (
    courseId: string,
    limit: number = 5,
  ): Promise<Course[]> => {
    const response = await api.get<{ success: boolean; data: Course[] }>(
      `/search/related/${courseId}?limit=${limit}`,
    );
    return response.success ? response.data : [];
  },
};
